import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { jwtDecode } from 'jwt-decode';
import api from "../../api/axios";
import { disconnectSocket } from "../../utils/socketManager";

const getAuthErrorMessage = (error, fallback) => {
    const rawMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        fallback;
    const message = String(rawMessage || fallback).trim();
    const lowerMessage = message.toLowerCase();

    if (
        lowerMessage.includes("already registered") ||
        lowerMessage.includes("already exists") ||
        lowerMessage.includes("already in use")
    ) {
        return "Email is already registered. Please log in or use forgot password.";
    }

    if (lowerMessage.includes("invalid otp") || lowerMessage.includes("incorrect otp")) {
        return "The OTP you entered is incorrect. Please check the code and try again.";
    }

    if (lowerMessage.includes("expired")) {
        return "This code has expired. Please request a new OTP.";
    }

    if (lowerMessage.includes("network")) {
        return "We could not connect to the server. Please check your internet connection and try again.";
    }

    return message || fallback;
};

export const register = createAsyncThunk("auth/register", async ({ email, password, name = "", photo = "", ref = null }, thunkAPI) => {
    try {
        const res = await api.post(`/api/auth/signup`, { email, password, name, photo, ref });
        return res.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(
            getAuthErrorMessage(error, "We could not create your account. Please try again.")
        );
    }
}
);

export const sendOtp = createAsyncThunk(
    "auth/sendOtp",
    async (email, thunkAPI) => {
        try {
            const res = await api.post("/api/auth/send-otp", { email });
            return res.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getAuthErrorMessage(error, "We could not send the OTP. Please try again.")
            );
        }
    }
);

export const verifyOtp = createAsyncThunk("auth/verifyOtp", async ({ email, otp }, thunkAPI) => {
    try {
        const res = await api.post("/api/auth/verify-otp", { email, otp });
        return res.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(
                getAuthErrorMessage(error, "The OTP could not be verified. Please try again.")
        );
    }
}
);

export const login = createAsyncThunk("auth/login", async ({ email, password }, thunkAPI) => {
    try {
        const res = await api.post(`/api/auth/login`, {
            email,
            password
        });
        return res.data;
    } catch (error) {
        // Check for specific error types
        if (error.response?.status === 401) {
            return thunkAPI.rejectWithValue("Incorrect email or password. Please try again.");
        }
        return thunkAPI.rejectWithValue(
            getAuthErrorMessage(error, "We could not sign you in. Please try again.")
        );
    }
}
);

export const onboardingQuestions = createAsyncThunk("auth/onboarding", async ({ jobTitle, previousIndustryYears, preferredIndustry, preferredDepartment }, thunkAPI) => {
    const token = localStorage.getItem("token");
    try {
        const res = await api.put(`/api/auth/onboarding`,
            { jobTitle, previousIndustryYears, preferredIndustry, preferredDepartment },
            {
                headers: {
                    "auth-token": token
                }
            }
        );
        return res.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(
            error.response?.data?.error || "Onboarding failed"
        );
    }
}
);

export const completeOnboarding = createAsyncThunk("auth/completeOnboarding", async (profileData, thunkAPI) => {
    const token = localStorage.getItem("token");
    try {
        // Try to save to backend if endpoint exists
        try {
            const res = await api.put(`/api/auth/complete-onboarding`,
                profileData,
                {
                    headers: {
                        "auth-token": token
                    }
                }
            );
            localStorage.setItem("hasCompletedOnboarding", "true");
            return res.data;
        } catch (apiError) {
            // If endpoint doesn't exist (404), just mark as complete locally
            if (apiError.response?.status === 404) {
                console.warn('Onboarding endpoint not found, marking as complete locally');
                localStorage.setItem("hasCompletedOnboarding", "true");
                return { success: true, message: "Onboarding completed (local only)" };
            }
            // Re-throw other errors
            throw apiError;
        }
    } catch (error) {
        // If all else fails, still mark as complete locally
        console.warn('Error completing onboarding, marking as complete locally:', error);
        localStorage.setItem("hasCompletedOnboarding", "true");
        return { success: true, message: "Onboarding completed (local only)" };
    }
}
);

export const fetchUserProfile = createAsyncThunk("auth/fetchUserProfile", async (_, thunkAPI) => {
    const token = localStorage.getItem("token");
    try {
        const res = await api.get(`/api/auth/getuser`, {
            headers: { "auth-token": token },
        });
        return res.data.user;
    } catch (error) {
        return thunkAPI.rejectWithValue("Failed to fetch profile");
    }
}
);

export const setUserFromToken = createAsyncThunk("auth/setUserFromToken", async (token) => {
    localStorage.setItem("token", token);
    const decoded = jwtDecode(token);
    localStorage.setItem("user", JSON.stringify(decoded));
    localStorage.setItem("loginSource", "google");

    const firstLogin = !decoded.onboardingCompleted;

    return { token, user: decoded, firstLogin };
}
);

export const pollForHiringSignalCompletion = () => async (dispatch, getState) => {
    const interval = setInterval(async () => {
        try {
            const result = await dispatch(fetchUserProfile());
            const user = result.payload;

            if (user?.initialHiringSignalScan) {
                console.log("User scan complete!");
                clearInterval(interval);
            } else {
                console.log("⏳ Still waiting for hiring signal scan to finish...");
            }
        } catch (err) {
            console.error("Error while polling user profile:", err);
            clearInterval(interval);
        }
    }, 10000);
};

export const forgotPassword = createAsyncThunk(
    "auth/forgotPassword",
    async (email, thunkAPI) => {
        try {
            const res = await api.post("/api/auth/forgotpass", { email });
            return res.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data?.message || "Failed to send password reset OTP"
            );
        }
    }
);

export const verifyResetPasswordOtp = createAsyncThunk(
    "auth/verifyResetPasswordOtp",
    async ({ email, otp }, thunkAPI) => {
        try {
            const res = await api.post("/api/auth/verifyresetpassotp", { email, otp });
            return res.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data?.message || "Invalid OTP"
            );
        }
    }
);

export const resetPassword = createAsyncThunk(
    "auth/resetPassword",
    async ({ email, otp, newPassword }, thunkAPI) => {
        try {
            const res = await api.post("/api/auth/resetpass", { email, otp, newPassword });
            return res.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data?.message || "Failed to reset password"
            );
        }
    }
);

export const updateUserProfile = createAsyncThunk(
    'auth/updateUserProfile',
    async (userData, { rejectWithValue, dispatch }) => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.put(`/api/auth/updateprofile`, userData,
                {
                    headers: {
                        'auth-token': token,
                    },
                    timeout: 15000,
                }
            );
            dispatch(fetchUserProfile());
            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                'Profile saving is temporarily unavailable. Please try again shortly.'
            );
        }
    }
);

export const updateEducation = createAsyncThunk(
    'auth/updateEducation',
    async ({ index, educationData }, thunkAPI) => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.patch(`/api/userprofile/updateeducation/${index}`,
                educationData,
                {
                    headers: {
                        'auth-token': token,
                    },
                }
            );
            return response.data.education;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data || { message: "Something went wrong" }
            );
        }
    }
);

export const deleteEducation = createAsyncThunk(
    'auth/deleteEducation',
    async (index, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("token");
            const response = await api.delete(`/api/userprofile/deleteeducation/${index}`,
                {
                    headers: {
                        'auth-token': token,
                    }
                }
            );
            return response.data.education;
        } catch (error) {
            console.error('Delete education error:', error);
            return rejectWithValue(error.response?.data || { error: 'Server error' });
        }
    }
);

export const updateExperience = createAsyncThunk(
    'auth/updateExperience',
    async ({ index, experienceData }, thunkAPI) => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.patch(`/api/userprofile/updateexperience/${index}`,
                experienceData,
                {
                    headers: {
                        'auth-token': token,
                    },
                }
            );
            return response.data.experience;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data || { message: "Something went wrong" }
            );
        }
    }
);

export const deleteExperience = createAsyncThunk(
    'auth/deleteExperience',
    async (index, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("token");
            const response = await api.delete(`/api/userprofile/deleteexperience/${index}`,
                {
                    headers: {
                        'auth-token': token,
                    }
                }
            );
            return response.data.experience;
        } catch (error) {
            console.error('Delete experience error:', error);
            return rejectWithValue(error.response?.data || { error: 'Server error' });
        }
    }
);

const authSlice = createSlice({
    name: "auth",
    initialState: {
        user: JSON.parse(localStorage.getItem("user")) || null,
        token: localStorage.getItem("token") || null,
        loginSource: localStorage.getItem("loginSource") || null,
        isGoogleConnected: false,
        hasPassword: true,
        // true default so existing email/password users aren't affected before first fetch
        firstLogin: false,
        onboardingActive: false,
        emailSentTo: null,
        loading: false,
        profileUpdateSuccess: false,
        error: null,
        otpVerified: false,
        verifiedEmail: null,
        passwordResetSuccess: false,
    },
    reducers: {
        logout: (state) => {
            state.user = null;
            state.token = null;
            state.loginSource = null;
            state.isGoogleConnected = false;
            state.hasPassword = true;
            state.error = null;
            state.firstLogin = false;
            state.emailSentTo = null;
            state.otpVerified = false;
            state.verifiedEmail = null;
            state.passwordResetSuccess = false;
            localStorage.clear();
            disconnectSocket();
        },
        setOnboardingActive: (state, action) => {
            state.onboardingActive = Boolean(action.payload);
        },
        setHasPassword: (state) => {
            state.hasPassword = true;
        },
        clearFirstLoginFlag: (state) => {
            state.firstLogin = false;
            if (state.user) {
                state.user.onboardingCompleted = true;
                localStorage.setItem("user", JSON.stringify(state.user));
            }
        },
        resetOtpState: (state) => {
            state.loading = false;
            state.error = null;
            state.emailSentTo = null;
            state.otpVerified = false;
            state.verifiedEmail = null;
            state.passwordResetSuccess = false;
        },
        clearPasswordResetSuccess: (state) => {
            state.passwordResetSuccess = false;
        },
        updateCredits: (state, action) => {
            if (state.user) {
                state.user.creditsBalance = action.payload;
                // Update localStorage to persist the change
                localStorage.setItem("user", JSON.stringify(state.user));
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(register.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(register.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload.user;
                state.token = action.payload.token;
                state.firstLogin = true;

                localStorage.setItem("token", action.payload.token);
                localStorage.setItem("user", JSON.stringify(action.payload.user));
                localStorage.setItem("loginSource", "mongo");

                state.emailSentTo = null;
                state.otpVerified = false;
                state.verifiedEmail = null;
            })
            .addCase(register.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(sendOtp.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.otpVerified = false;
            })
            .addCase(sendOtp.fulfilled, (state, action) => {
                state.loading = false;
                state.emailSentTo = action.payload.data.email;
                state.otpVerified = false;
            })
            .addCase(sendOtp.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(verifyOtp.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(verifyOtp.fulfilled, (state, action) => {
                state.loading = false;
                state.otpVerified = true;
                state.verifiedEmail = action.payload.email;
                state.error = null;
            })
            .addCase(verifyOtp.rejected, (state, action) => {
                state.loading = false;
                state.otpVerified = false;
                state.error = action.payload || "OTP verification failed";
            })

            .addCase(login.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload.user;
                state.token = action.payload.token;
                state.loginSource = "mongo";

                state.isGoogleConnected = action.payload.user.isGoogleConnected;
                state.hasPassword = action.payload.user.hasPassword ?? true;
                state.firstLogin = !action.payload.user.onboardingCompleted;

                localStorage.setItem("token", action.payload.token);
                localStorage.setItem("user", JSON.stringify(action.payload.user));
                localStorage.setItem("loginSource", "mongo");

                state.emailSentTo = null;
                state.otpVerified = false;
                state.verifiedEmail = null;
            })
            .addCase(login.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(setUserFromToken.fulfilled, (state, action) => {
                state.user = action.payload.user;
                state.token = action.payload.token;
                state.loginSource = "google";
                state.firstLogin = action.payload.firstLogin;

                state.emailSentTo = null;
                state.otpVerified = false;
                state.verifiedEmail = null;
            })

            .addCase(onboardingQuestions.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(onboardingQuestions.fulfilled, (state, action) => {
                state.loading = false;
                if (state.user) {
                    state.user.onboardingCompleted = true;
                }
                state.firstLogin = false;
            })
            .addCase(onboardingQuestions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Failed to save onboarding data";
            })

            .addCase(fetchUserProfile.fulfilled, (state, action) => {
                state.user = action.payload;
                state.isGoogleConnected = action.payload.isGoogleConnected;
                state.hasPassword = action.payload.hasPassword ?? true;
            })

            .addCase(forgotPassword.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.passwordResetSuccess = false;
            })
            .addCase(forgotPassword.fulfilled, (state, action) => {
                state.loading = false;
                state.emailSentTo = action.payload.data.email;
                state.error = null;
                state.passwordResetSuccess = false;
            })
            .addCase(forgotPassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.passwordResetSuccess = false;
            })

            .addCase(verifyResetPasswordOtp.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.passwordResetSuccess = false;
            })
            .addCase(verifyResetPasswordOtp.fulfilled, (state, action) => {
                state.loading = false;
                state.otpVerified = true;
                state.verifiedEmail = action.payload.email;
                state.error = null;
                state.passwordResetSuccess = false;
            })
            .addCase(verifyResetPasswordOtp.rejected, (state, action) => {
                state.loading = false;
                state.otpVerified = false;
                state.error = action.payload || "OTP verification failed";
                state.passwordResetSuccess = false;
            })

            .addCase(resetPassword.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.passwordResetSuccess = false;
            })
            .addCase(resetPassword.fulfilled, (state, action) => {
                state.loading = false;
                state.passwordResetSuccess = true;
                state.error = null;
                state.emailSentTo = null;
                state.otpVerified = false;
                state.verifiedEmail = null;
            })
            .addCase(resetPassword.rejected, (state, action) => {
                state.loading = false;
                state.passwordResetSuccess = false;
                state.error = action.payload;
            })

            .addCase(updateUserProfile.pending, (state) => {
                state.loading = true;
                state.profileUpdateSuccess = false;
            })
            .addCase(updateUserProfile.fulfilled, (state, action) => {
                state.loading = false;
                state.profileUpdateSuccess = true;
                state.user = {
                    ...state.user,
                    ...action.payload
                };
            })
            .addCase(updateUserProfile.rejected, (state, action) => {
                state.loading = false;
                state.profileUpdateSuccess = false;
                state.error = action.payload;
            })

            .addCase(updateEducation.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateEducation.fulfilled, (state, action) => {
                state.loading = false;
                if (state.user) {
                    state.user.education = action.payload;
                }
            })
            .addCase(updateEducation.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Update education failed";
            })

            .addCase(deleteEducation.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteEducation.fulfilled, (state, action) => {
                state.loading = false;
                if (state.user) {
                    state.user.education = action.payload;
                }
            })
            .addCase(deleteEducation.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || 'Failed to delete education';
            })

            .addCase(updateExperience.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateExperience.fulfilled, (state, action) => {
                state.loading = false;
                if (state.user) {
                    state.user.experience = action.payload;
                }
            })
            .addCase(updateExperience.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Update experience failed";
            })

            .addCase(deleteExperience.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteExperience.fulfilled, (state, action) => {
                state.loading = false;
                if (state.user) {
                    state.user.experience = action.payload;
                }
            })
            .addCase(deleteExperience.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || 'Failed to delete experience';
            });
    },
});

export const { logout, clearFirstLoginFlag, setOnboardingActive, resetOtpState, clearPasswordResetSuccess, updateCredits, setHasPassword } = authSlice.actions;
export default authSlice.reducer;
